import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import LayoutContainer from 'components/Common/LayoutContainer'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import OfferListHeading from 'components/OfferList/OfferListHeading'
import { OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import getOfferListKey from 'lib/offer/offerListKey'
import Heading from 'LuxKit/Typography/Heading'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferListOfferTile from 'components/OfferList/OfferListTiles/OfferListOfferTile'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'

interface Props extends App.HubPageComponentOffersLoader {
  content?: App.ContentHubPage
  urlFilters: App.OfferListFilters
  availableFilters: App.OfferListAvailableFilters
  offerTypes: Array<App.OfferType>
}

function OffersLoader(props: Props) {
  const intl = useIntl()
  const { urlFilters, filters, inCarousel, content, offersToShow, title, offerTypes } = props

  const combinedFilters = useMemo(() => ({
    ...urlFilters,
    ...filters,
    limit: offersToShow,
    frontPageVisibleOnly: true,
  }), [urlFilters, filters, offersToShow])

  const soldOutTitle = content ? intl.formatMessage({ id: '3NhwtgeeMnstrMdmfJJE', defaultMessage: 'We are currently sold out of our {destination} escapes!', description: 'Heading shown when no offers available for a destination on hub page' }, { destination: content.title }) : ''
  const soldOutDescription = content ? intl.formatMessage({ id: 'tFckGUy_nbmLqyp5JGo0', defaultMessage: 'Our travel experts are working hard on curating new {destination} offers. In the meantime check out our available offers in other exciting destinations.', description: 'Message shown when no offers available for a destination on hub page' }, { destination: content.title }) : ''

  const offerListExtras: Array<OfferListV2Item> = content ? [
    {
      id: 'heading',
      position: 'start',
      element: <OfferListHeading
        title={soldOutTitle}
        description={soldOutDescription}
      />,
      show: 'hasNoOffers',
    },
  ] : []

  const listOfferTypes = (offerTypes ?? FLASH_OFFERS_TYPES).filter(type => type !== OFFER_TYPE_LAST_MINUTE)

  const offerList = useOfferList({
    ...combinedFilters,
    offerTypes: listOfferTypes,
  }, { disabled: !inCarousel })

  const additionalOfferSearch = useMemo(() => buildSearchStringFromFilters(combinedFilters), [combinedFilters])

  return <VerticalSpacer gap={32}>
    <LayoutContainer>
      <VerticalSpacer gap={32}>
        <Heading variant="heading3" as="h2" align="center">{title}</Heading>
        {!inCarousel && <OfferList
          interstitials={offerListExtras}
          maxOffers={offersToShow}
          filters={{ ...combinedFilters, offerTypes: listOfferTypes }}
          tileStyle="search"
        />}
        {inCarousel && !offerList.fetching && <>
          {offerList.offerIds.length === 0 && content && <OfferListHeading
            title={soldOutTitle}
            description={soldOutDescription}
          />}
          {offerList.offerIds.length === 1 && <OfferListOfferTile
            offerId={offerList.offerIds[0]}
            tileStyle="search"
            position={0}
          />}
        </>}
      </VerticalSpacer>
    </LayoutContainer>
    {inCarousel && offerList.offerIds.length > 1 &&
      <OfferListCarousel
        overrideOfferList={offerList}
        additionalOfferUrlQuery={additionalOfferSearch}
      />}
  </VerticalSpacer>
}

const mapStateToProps = (state: App.State, ownProps: Props) => {
  const urlFilters = getUrlOfferListFilters(state)
  const key = ownProps.offerTypes && getOfferListKey({
    ...urlFilters,
    ...ownProps.filters,
    offerTypes: ownProps.offerTypes.filter(type => type !== OFFER_TYPE_LAST_MINUTE),
  })

  return ({
    urlFilters,
    availableFilters: state.offer.offerListFilterOptions[key]?.filters,
  })
}

export default connect(mapStateToProps)(OffersLoader)
